import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Divider, Grid, Link, Typography, styled, Tooltip } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { format } from '@e3dc-react/shell/Libs/date'
import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'

import { Installation } from 'src/Libs/installations/models/installation.model'
import { getFullName } from 'src/Libs/utils/string-functions'
import { getEmsProductImage } from 'src/Libs/installations/utils/product-image'
import PaperContainer from '../PaperContainer'
import { User } from 'src/State/User/UserModel'
import { StorageModel } from 'src/Libs/storage/models'
import InfoText from '../InfoText'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import useModals from 'src/Hooks/useModals'
import { useDeleteSystem } from '../../hooks/use-system'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { DeviceLinkModel } from 'src/Libs/installations/models'
import { useFarmContext } from 'src/Elements/Farm'
import WarningMessage from 'src/Elements/Messages/Warning'

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.grey.grey06,
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
}))

interface InformationProps {
  storage: StorageModel
  storageId: string | undefined
  installationId: string
  installation: Installation | null
  installerUser?: User
  deviceLinks: DeviceLinkModel[]
}

const useStyles = makeStyles()(theme => ({
  linkButton: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    '&:hover': {
      cursor: 'pointer',
    },
  },
}))

interface DeleteButtonProps {
  onClick: () => void
  disabled?: boolean
}

const DeleteButton: FC<DeleteButtonProps> = ({ disabled, onClick }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <LinkButton
      linkClassName={classes.linkButton}
      content={
        <Typography variant="caption" fontWeight="500">
          {t('system-details.components.delete-system.delete')}
        </Typography>
      }
      onClick={onClick}
      clickable={!disabled}
    />
  )
}

/**
 * Component to hold basic information about a system/Storage
 */
const SystemInformation: FC<InformationProps> = ({ installationId, installation, storage, installerUser, storageId, deviceLinks }) => {
  const { t } = useCustomTranslation()
  const storageImage = getEmsProductImage(storage.product, installation?.installationStatus)
  const fullInstallerName = getFullName(installerUser?.userData?.Vorname, installerUser?.userData?.Name)
  const installerCompany = installerUser?.company
  const { openReasonDialog } = useModals()
  const { isLoading, isFarmMasterSelected } = useFarmContext()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const navigate = useNavigate()
  const emsMasterDeviceId = installation?.emsMasterDeviceId

  const deviceLinkForSystem = deviceLinks.find(deviceLink => deviceLink.deviceId === storageId)
  const allOtherActiveDevices = deviceLinks?.filter(deviceLink => deviceLink.deviceId !== storageId && !deviceLink.decommissionedAt)

  const showDecomissionButton = !isLoading && !deviceLinkForSystem?.decommissionedAt
  const isDeleteButtonDisabled = isFarmMasterSelected && !!allOtherActiveDevices.length

  const { mutateAsync: deleteSystem } = useDeleteSystem({
    onSuccess: () => {
      enqueueSuccessStack(t('system-details.components.delete-system.delete-success'))
      navigate(`/overview/installations/${installationId}`, { replace: true })
    },
    onError: () => enqueueErrorStack(t('system-details.components.delete-system.delete-failed')),
  })

  const handleDeleteClick = (): void => {
    openReasonDialog({
      title: t('system-details.components.delete-system.delete'),
      description: (
        <>
          {t('system-details.components.delete-system.text')}
          {allOtherActiveDevices.length <= 1 && (
            <WarningMessage text={t('system-details.components.delete-system.last-system-text')} sx={{ mt: 2 }} />
          )}
        </>
      ),
      onSubmit: async payload => {
        await deleteSystem({ installationId, deviceLinkId: deviceLinkForSystem?.id, payload })
      },
    })
  }

  return (
    <PaperContainer title={t('system-details.informations.title')}>
      <Grid container padding={2} display="flex" alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <InfoText
            icon={storageImage && <img src={storageImage} alt="product-icon" />}
            title={t('system-details.informations.type')}
            value={storage.product}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          {emsMasterDeviceId && <InfoText title={t('system-details.informations.ems-master-id')} value={installation?.emsMasterDeviceId} />}
        </Grid>
        {showDecomissionButton && (
          <Grid item xs={12} sm={4} md={6} display="flex" justifyContent="flex-end">
            {isDeleteButtonDisabled ? (
              <Tooltip title={t('system-details.components.delete-system.delete-disabled-tooltip')}>
                <span>
                  <DeleteButton onClick={handleDeleteClick} disabled />
                </span>
              </Tooltip>
            ) : (
              <DeleteButton onClick={handleDeleteClick} />
            )}
          </Grid>
        )}
      </Grid>
      <StyledDivider />
      <Grid container padding={2}>
        <Grid item xs={12}>
          <Typography variant="h4">{t('system-details.informations.software')}</Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText title={t('system-details.informations.software-version')} value={storage.softwareRelease} />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <InfoText
            title={t('system-details.informations.installation-date')}
            value={format(new Date(storage.lastUpdate), t('common.format.date-fns-date-time'))}
          />
        </Grid>
      </Grid>
      {installerUser && (
        <>
          <StyledDivider />
          <Grid container padding={2}>
            <Grid item xs={12}>
              <Typography variant="h4">{t('common.installer')}</Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InfoText
                title={t('common.name')}
                value={
                  fullInstallerName && storage?.installerId ? (
                    <Link component={RouterLink} variant="body2" sx={{ textDecoration: 'none' }} to={`/overview/users/${storage.installerId}`}>
                      {fullInstallerName}
                    </Link>
                  ) : (
                    '-'
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <InfoText
                title={t('common.company')}
                value={
                  installerCompany?.id && installerCompany?.Name ? (
                    <Link component={RouterLink} variant="body2" sx={{ textDecoration: 'none' }} to={`/overview/companies/${installerCompany.id}`}>
                      {installerCompany.Name}
                    </Link>
                  ) : (
                    '-'
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <InfoText title={t('common.email')} value={installerUser?.contactData.Email || '-'} />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <InfoText title={t('common.phone')} value={installerUser?.contactData.Telefon || '-'} />
            </Grid>
          </Grid>
        </>
      )}
    </PaperContainer>
  )
}

export default SystemInformation
